import React, { useEffect } from "react";
import { Input, Button, Card, Row, Col } from "antd";
import { Typography } from '@mui/material';
import { CalculatorOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Button as MUIButton } from '@mui/material';
import BadgeButton from "components/AdminBooks/BadgeButton";

const default_value = [
  {
    title: "Nativos 1",
    min: 0,
    max: 8,
    color: "#003366",
    description:
      "El estudiante muestra un conocimiento inicial o limitado del español. En esta etapa, las habilidades básicas de comprensión y expresión lingüística están emergiendo. Los estudiantes necesitarán apoyo intensivo para mejorar su fluidez y comprensión básica del idioma.",
  },
  {
    title: "Nativos 2",
    min: 9,
    max: 19,
    color: "#f58d1d",
    description:
      "El estudiante tiene una comprensión y capacidad de expresión moderadas en español. Puede comunicarse en situaciones cotidianas con limitaciones y mostrar entendimiento de conceptos simples. La instrucción dirigida y la práctica continua serán cruciales para su avance.",
  },
  {
    title: "Nativos 3",
    min: 20,
    max: 27,
    color: "#40a9ff",
    description:
      "El estudiante posee una buena habilidad en español, manejando la comunicación en el idioma con buena fluidez y pocas restricciones en contextos conocidos. Aunque aún puede beneficiarse de la enseñanza enfocada en aspectos complejos del idioma.",
  },
  {
    title: "AP Spanish",
    min: 28,
    max: 32,
    color: "#a084ca",
    description:
      "El estudiante demuestra un alto dominio del español, equivalente al esperado en un hablante nativo con educación formal. Este nivel indica que el estudiante está preparado para enfrentar desafíos lingüísticos a nivel avanzado y discusiones académicas complejas.",
  },
];

const predefinedColors = [
  "#003366", "#f58d1d", "#40a9ff", "#a084ca",
  "#ff4d4f", "#36cfc9", "#9254de", "#ffc53d",
  "#597ef7", "#ff7a45", "#73d13d", "#faad14",
  "#5cdbd3", "#2f54eb", "#d3adf7", "#ff85c0",
  "#bae637", "#ffd666", "#69c0ff", "#ffccc7",
];
function getRandomColor(levels = []) {
  const usedColors = levels.map(level => level.color);
  const availableColors = predefinedColors.filter(color => !usedColors.includes(color));

  // Fallback to any if all are used
  const pool = availableColors.length > 0 ? availableColors : predefinedColors;

  const randomIndex = Math.floor(Math.random() * pool.length);
  return pool[randomIndex];
}
const LevelManager = ({ levels = [], onChange, disabled = false, max_value }) => {
  const defaultLevel = {
    title: "",
    min: "",
    max: "",
    description: "",
  };

  useEffect(() => {
    if (!disabled && levels.length === 0) {
      onChange(default_value);
    }
  }, [levels, disabled, onChange]);

  const handleChange = (index, field, value) => {
    const updated = [...levels];
    updated[index][field] = value;
    onChange(updated);
  };

  const handleAddLevel = (index = null) => {
    const updated = [...levels];
    if (index !== null) {
      updated.splice(index + 1, 0, { ...defaultLevel, color: getRandomColor(levels) });
    } else {
      updated.push({ ...defaultLevel, color: getRandomColor(levels) });
    }
    onChange(updated);
  };

  const handleDeleteLevel = (index) => {
    const updated = [...levels];
    updated.splice(index, 1);
    onChange(updated);
  };

  const handleCalculateRange = (max_point) => {
    const levelCount = Math.max(1, levels.length);
    const maxVal = Math.max(0, Math.floor(Number(max_point) || 0));
    const total = maxVal + 1;
    const baseSize = Math.floor(total / levelCount);
    const remainder = total % levelCount;
    const newLevels = [];
    let current = 0;
    for (let i = 0; i < levelCount; i++) {
      const segmentSize = baseSize + (i < remainder ? 1 : 0);
      const min = current;
      const max = current + segmentSize - 1;
      current = max + 1;
      const existing = levels[i] || default_value[i];
      newLevels.push({
        title: existing?.title ?? `Level ${i + 1}`,
        min,
        max,
        description: existing?.description ?? "",
        color: existing?.color || predefinedColors[i] || "#003366",
      });
    }
    onChange(newLevels);
  };

  return (
    <div style={{ width: "100%", marginTop: 24 }}>
      <div className="flex items-center justify-between mb-10px">
        <Typography variant="h6" gutterBottom>
          Levels Configuration
        </Typography>
        <div className="flex items-center gap-10px">
          <div style={{whiteSpace: "nowrap"}}>
            Max Points: <b>{max_value}</b>
          </div>
          <BadgeButton 
             style={{ width: "fit-content" }} 
             variant="contained"
             color="primary"
             startIcon={<CalculatorOutlined />}
             onClick={() => handleCalculateRange(max_value)} 
             disabled={!max_value && max_value !== 0}>Calculate Range</BadgeButton>
        </div>
      </div>
      {levels.map((level, index) => (
        <Card
          key={index}
          style={{ marginBottom: 16 }}
          title={`Level ${index + 1}`}
          extra={
            !disabled && (
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDeleteLevel(index)}
              />
            )
          }
        >
          <Row gutter={12}>
            <Col span={12}>
              <Input
                placeholder="Level Title"
                value={level.title}
                onChange={(e) => handleChange(index, "title", e.target.value)}
                disabled={disabled}
              />
            </Col>
            <Col span={6}>
              <Input
                type="number"
                placeholder="Min"
                value={level.min}
                onChange={(e) => handleChange(index, "min", e.target.value)}
                disabled={disabled}
              />
            </Col>
            <Col span={6}>
              <Input
                type="number"
                placeholder="Max"
                value={level.max}
                onChange={(e) => handleChange(index, "max", e.target.value)}
                disabled={disabled}
              />
            </Col>
          </Row>
          <Row style={{ marginTop: 12 }}>
            <Col span={24}>
              <Typography variant="subtitle2" gutterBottom>
                Level Description
              </Typography>
              <Input.TextArea
                rows={3}
                placeholder="Level Description"
                value={level.description}
                onChange={(e) =>
                  handleChange(index, "description", e.target.value)
                }
                disabled={disabled}
              />
            </Col>
          </Row>
          <Row style={{ marginTop: 12 }}>
            <Col span={24}>
              <Typography variant="subtitle2" gutterBottom>
                Level Description (English)
              </Typography>
              <Input.TextArea
                rows={3}
                placeholder="Level Description(English)"
                value={level.description_english}
                onChange={(e) =>
                  handleChange(index, "description_english", e.target.value)
                }
                disabled={disabled}
              />
            </Col>
          </Row>
          <Row style={{ marginTop: 12 }}>
            <Col span={24}>
              <Typography variant="subtitle2" gutterBottom>
                Color
              </Typography>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <input
                  type="color"
                  value={level.color || "#003366"}
                  disabled={disabled}
                  onChange={(e) => handleChange(index, "color", e.target.value)}
                  style={{
                    width: 40,
                    height: 36,
                    padding: 2,
                    border: "1px solid #d9d9d9",
                    borderRadius: 6,
                    cursor: disabled ? "not-allowed" : "pointer",
                  }}
                />
                {/* <Input
                  value={level.color || ""}
                  placeholder="#003366"
                  disabled={disabled}
                  onChange={(e) => handleChange(index, "color", e.target.value)}
                  style={{ width: 120 }}
                /> */}
              </div>
            </Col>
          </Row>

          {!disabled && (
            <Row justify="end" style={{ marginTop: 10 }}>
              <Button type="dashed" onClick={() => handleAddLevel(index)}>
                Add Below
              </Button>
            </Row>
          )}
        </Card>
      ))}
      {!disabled && (
        <Row justify="start" style={{ marginTop: 8 }}>
          <MUIButton
            variant="contained"
            color="primary"
            startIcon={<PlusOutlined />}
            onClick={() => handleAddLevel(null)}
          >
            Add Level
          </MUIButton>
        </Row>
      )}
    </div>
  );
};

export default LevelManager;